import React, { useState, useEffect, useCallback } from 'react'
import styled from 'styled-components'
import { CircularProgress } from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import VerifiedOutlinedIcon from '@mui/icons-material/VerifiedOutlined'
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined'
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined'
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined'
import {
  listMyCertifications,
  createMyCertification,
  updateMyCertification,
  deleteMyCertification,
  downloadMyCertificationsCsv,
} from 'services/certificationService'
import { downloadBlob } from 'utils/downloadBlob'

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const Description = styled.p`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.grey[200]};
  margin: 0 0 8px;
`

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  background: #fff;
  border: 1px solid ${({ theme }) => theme.colors.lightGrey};
  border-radius: 8px;
  padding: 10px 14px;
`

const StatusIcon = styled.div`
  color: ${({ $tone }) => $tone};
  display: flex;
  align-items: center;
`

const Body = styled.div`
  flex: 1;
  min-width: 0;
`

const Name = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.haiti};
`

const Meta = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.grey[200]};
`

const DeleteBtn = styled.button`
  background: transparent;
  border: none;
  color: ${({ theme }) => theme.colors.grey[200]};
  cursor: pointer;
  padding: 4px;

  &:hover { color: ${({ theme }) => theme.colors.error}; }
`

const ButtonRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`

const AddBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: ${({ theme }) => theme.colors.primary};
  color: #fff;
  border: none;
  border-radius: 6px;
  padding: 8px 14px;
  font-size: 13px;
  cursor: pointer;

  &:hover { opacity: 0.9; }
`

const ExportBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #fff;
  color: ${({ theme }) => theme.colors.haiti};
  border: 1px solid ${({ theme }) => theme.colors.lightGrey};
  border-radius: 6px;
  padding: 8px 14px;
  font-size: 13px;
  cursor: pointer;

  &:hover { background: ${({ theme }) => theme.colors.backgroundColor}; }
`

const Form = styled.form`
  background: ${({ theme }) => theme.colors.backgroundColor};
  border-radius: 8px;
  padding: 14px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
`

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const Label = styled.label`
  font-size: 12px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.haiti};
`

const Input = styled.input`
  border: 1px solid ${({ theme }) => theme.colors.lightGrey};
  border-radius: 6px;
  padding: 6px 10px;
  font-size: 13px;
  outline: none;
  &:focus { border-color: ${({ theme }) => theme.colors.primary}; }
`

const FormFooter = styled.div`
  grid-column: 1 / -1;
  display: flex;
  gap: 8px;
  justify-content: flex-end;
`

const Btn = styled.button`
  border: 1px solid ${({ theme }) => theme.colors.lightGrey};
  background: ${({ $primary, theme }) => $primary ? theme.colors.primary : '#fff'};
  color: ${({ $primary }) => $primary ? '#fff' : '#0f172a'};
  border-radius: 6px;
  padding: 6px 12px;
  font-size: 13px;
  cursor: pointer;
`

const Empty = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.grey[200]};
  font-style: italic;
`

const STATUS_VIEW = {
  valid:         { tone: '#16a34a', label: 'Valid',         Icon: VerifiedOutlinedIcon },
  expiring_soon: { tone: '#ea580c', label: 'Expiring soon', Icon: WarningAmberOutlinedIcon },
  expired:       { tone: '#dc2626', label: 'Expired',       Icon: ErrorOutlineOutlinedIcon },
  verified:      { tone: '#16a34a', label: 'Verified',      Icon: VerifiedOutlinedIcon },
  unverified:    { tone: '#64748b', label: 'Unverified',    Icon: VerifiedOutlinedIcon },
}

const emptyForm = { name: '', issuer: '', referenceNumber: '', issuedAt: '', expiresAt: '' }

const CertificationsPanel = () => {
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(true)
  const [showAdd, setShowAdd] = useState(false)
  const [editingId, setEditingId] = useState(null)
  const [submitting, setSubmitting] = useState(false)
  const [form, setForm] = useState(emptyForm)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const res = await listMyCertifications()
      setItems(res.data?.data || [])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load() }, [load])

  const resetForm = () => {
    setForm(emptyForm)
    setShowAdd(false)
    setEditingId(null)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!form.name.trim()) return
    setSubmitting(true)
    try {
      const payload = {
        name: form.name.trim(),
        issuer: form.issuer.trim() || null,
        referenceNumber: form.referenceNumber.trim() || null,
        issuedAt: form.issuedAt || null,
        expiresAt: form.expiresAt || null,
      }
      if (editingId) {
        await updateMyCertification(editingId, payload)
      } else {
        await createMyCertification(payload)
      }
      resetForm()
      await load()
    } finally {
      setSubmitting(false)
    }
  }

  const handleEdit = (cert) => {
    setForm({
      name: cert.name || '',
      issuer: cert.issuer || '',
      referenceNumber: cert.referenceNumber || '',
      issuedAt: cert.issuedAt || '',
      expiresAt: cert.expiresAt || '',
    })
    setEditingId(cert.id)
    setShowAdd(true)
  }

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete certification "${name}"?`)) return
    await deleteMyCertification(id)
    await load()
  }

  return (
    <Wrap>
      <Description>
        Track your professional certifications and training records.
        You'll get a reminder 90, 30, and 7 days before each one expires — and again on the day it lapses.
      </Description>
      {loading ? <CircularProgress size={18} /> : items.length === 0 ? (
        <Empty>No certifications recorded yet.</Empty>
      ) : (
        <List>
          {items.map(c => {
            const view = STATUS_VIEW[c.status] || STATUS_VIEW.unverified
            const Icon = view.Icon
            return (
              <Row key={c.id}>
                <StatusIcon $tone={view.tone}><Icon /></StatusIcon>
                <Body>
                  <Name>{c.name}</Name>
                  <Meta>
                    {c.issuer ? `${c.issuer} · ` : ''}
                    {c.expiresAt
                      ? `Expires ${new Date(c.expiresAt).toLocaleDateString()} (${view.label})`
                      : view.label}
                  </Meta>
                </Body>
                <DeleteBtn onClick={() => handleEdit(c)} title="Edit">
                  <EditOutlinedIcon fontSize="small" />
                </DeleteBtn>
                <DeleteBtn onClick={() => handleDelete(c.id, c.name)} title="Delete">
                  <DeleteOutlineIcon fontSize="small" />
                </DeleteBtn>
              </Row>
            )
          })}
        </List>
      )}

      {!showAdd ? (
        <ButtonRow>
          <AddBtn onClick={() => setShowAdd(true)}>
            <AddIcon style={{ fontSize: 16 }} /> Add certification
          </AddBtn>
          {items.length > 0 && (
            <ExportBtn
              type="button"
              title="Download your certifications as CSV"
              onClick={async () => {
                const res = await downloadMyCertificationsCsv()
                downloadBlob(res?.data, `certifications-${new Date().toISOString().slice(0, 10)}.csv`)
              }}
            >
              <FileDownloadOutlinedIcon style={{ fontSize: 16 }} /> Export CSV
            </ExportBtn>
          )}
        </ButtonRow>
      ) : (
        <Form onSubmit={handleSubmit}>
          <Field>
            <Label>{editingId ? 'Edit certification' : 'New certification'}</Label>
          </Field>
          <Field>
            <Label>Name *</Label>
            <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. CSCS Black Card" />
          </Field>
          <Field>
            <Label>Issuer</Label>
            <Input value={form.issuer} onChange={e => setForm(f => ({ ...f, issuer: e.target.value }))} placeholder="e.g. CSCS, IOSH, NEBOSH" />
          </Field>
          <Field>
            <Label>Reference number</Label>
            <Input value={form.referenceNumber} onChange={e => setForm(f => ({ ...f, referenceNumber: e.target.value }))} />
          </Field>
          <Field>
            <Label>Issued on</Label>
            <Input type="date" value={form.issuedAt} onChange={e => setForm(f => ({ ...f, issuedAt: e.target.value }))} />
          </Field>
          <Field>
            <Label>Expires on</Label>
            <Input type="date" value={form.expiresAt} onChange={e => setForm(f => ({ ...f, expiresAt: e.target.value }))} />
          </Field>
          <FormFooter>
            <Btn type="button" onClick={resetForm} disabled={submitting}>Cancel</Btn>
            <Btn type="submit" $primary disabled={submitting || !form.name.trim()}>
              {submitting ? 'Saving…' : (editingId ? 'Update' : 'Save')}
            </Btn>
          </FormFooter>
        </Form>
      )}
    </Wrap>
  )
}

export default CertificationsPanel
